import { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import {
  X, Loader2, TrendingUp, Sparkles, PenLine, Wand2, RefreshCw, Check, ArrowRight,
  Lightbulb, GraduationCap, Star, Info, PartyPopper, ArrowLeft, BookOpen, ShieldCheck,
  Pencil, RotateCcw, Mic, MicOff, HelpCircle,
} from "lucide-react";
import { MatchScoreBadge } from "./MatchScoreBadge";
import ConfettiBurst from "@/components/ConfettiBurst";

const CATEGORY_ICONS = {
  skills: Star, experience: Lightbulb, wording: PenLine, learning: GraduationCap,
};

const STEPS = [
  { key: "advice", labelKey: "imwStepAdvice", Icon: Sparkles },
  { key: "rewrite", labelKey: "imwStepRewrite", Icon: PenLine },
  { key: "regenerate", labelKey: "imwStepRegen", Icon: Wand2 },
  { key: "result", labelKey: "imwStepResult", Icon: PartyPopper },
];

/**
 * Four-step wizard: advice → CV rewrite → regenerate letter → result.
 * Each step opens with a "wat gebeurt hier" panel so the user understands
 * the AI operations before triggering them.
 */
export default function ImproveMatchWizard({ letterId, letterJobTitle = "", currentScore, onLetterUpdated, onClose }) {
  const { t } = useT();
  const [stepIdx, setStepIdx] = useState(0);

  // Step 1: advice
  const [advice, setAdvice] = useState(null);
  const [adviceLoading, setAdviceLoading] = useState(true);
  // Per-tip user context — key = tip index. Each entry:
  //   { input: str, experience_ids: string[] }
  // Sent to the rewrite endpoint so the LLM knows which criteria carry
  // user-provided clarifications and which experiences they apply to.
  const [tipContexts, setTipContexts] = useState({});

  // Step 2: rewrite preview.
  // `rewrite` is the AI's original proposal (frozen — used as the reset
  // baseline). `editedRewrite` mirrors it but tracks any in-place edits
  // the user makes before accepting. Two-state design so "Herstel
  // origineel" can always snap back to the AI's exact wording.
  const [rewrite, setRewrite] = useState(null);
  const [editedRewrite, setEditedRewrite] = useState(null);
  const [rewriteLoading, setRewriteLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  // Optional per-fork title swap — when the default CV's professional
  // title differs from the vacancy's job_title, the wizard offers to
  // rename `personal.title` on the fork only. Auto-checked when the
  // rewrite loads with a title mismatch (per user's ux feedback: a
  // Business-Analist vacancy should default to swapping a "Sr.
  // Projectleider" CV title). User can still uncheck to preserve
  // legacy behavior. The default CV is NEVER touched regardless.
  const [swapTitle, setSwapTitle] = useState(false);
  const [swapTitleAutoSet, setSwapTitleAutoSet] = useState(false);
  // The vacancy's title is passed in from LetterEditor as a prop;
  // resolved locally so the sub-components don't need the whole letter.
  const letterTitle = letterJobTitle || "";

  // Step 3: regenerate letter
  const [regenLoading, setRegenLoading] = useState(false);

  // Step 4: result
  const [result, setResult] = useState(null);
  const [confetti, setConfetti] = useState(false);
  // Rollback state — set when the user clicks "Terugdraaien" on the
  // result step after seeing the score dropped. Closes the wizard after
  // the letter is restored to its pre-improve state.
  const [rollingBack, setRollingBack] = useState(false);
  const [rolledBack, setRolledBack] = useState(false);

  const loadAdvice = useCallback(async () => {
    setAdviceLoading(true);
    try {
      const { data } = await api.post(`/letters/${letterId}/improve-tips`);
      setAdvice(data);
      // Seed per-tip context from any user_input the backend pre-filled
      // (currently: the employer's Verduidelijking-gevraagd note, which
      // is inserted as a priority tip so the AI weaves that requirement
      // into the CV/letter automatically). Users can still edit it.
      const seeded = {};
      (data?.tips || []).forEach((tip, i) => {
        const pre = (tip?.user_input || "").trim();
        if (pre) seeded[i] = { input: pre, experience_ids: [] };
      });
      if (Object.keys(seeded).length) setTipContexts(seeded);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("ltImproveFailed"));
      onClose();
    } finally {
      setAdviceLoading(false);
    }
  }, [letterId, t, onClose]);

  useEffect(() => { loadAdvice(); }, [loadAdvice]);

  const loadRewrite = useCallback(async () => {
    if (!advice) return;
    setRewriteLoading(true);
    try {
      // Enrich each tip with the user's per-tip clarification + which
      // experiences it applies to. Backend picks these up as prominent
      // "User note" hints in the rewrite prompt.
      const enrichedTips = (advice.tips || []).map((tip, i) => {
        const ctx = tipContexts[i];
        if (!ctx || (!ctx.input && !(ctx.experience_ids || []).length)) return tip;
        return {
          ...tip,
          user_input: (ctx.input || "").trim(),
          experience_ids: ctx.experience_ids || [],
        };
      });
      const { data } = await api.post(`/letters/${letterId}/preview-rewrite`, { tips: enrichedTips });
      setRewrite(data);
      // Seed the editable mirror with the AI's suggestion. Deep-cloning
      // via JSON round-trip is fine here — payload is small text + arrays.
      setEditedRewrite(JSON.parse(JSON.stringify(data)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("ltRewriteFailed"));
      // Stay on advice step so user isn't stuck.
      setStepIdx(0);
    } finally {
      setRewriteLoading(false);
    }
  }, [advice, letterId, t]);

  // Auto-check the title-swap toggle the first time a rewrite arrives with
  // a title mismatch. Users can still uncheck; we track `swapTitleAutoSet`
  // to avoid clobbering a manual uncheck if the rewrite reloads.
  useEffect(() => {
    if (!rewrite || swapTitleAutoSet) return;
    const cur = ((rewrite.personal_title || "") + "").trim().toLowerCase();
    const tgt = (letterTitle || "").trim().toLowerCase();
    if (cur && tgt && cur !== tgt) {
      setSwapTitle(true);
    }
    setSwapTitleAutoSet(true);
  }, [rewrite, letterTitle, swapTitleAutoSet]);

  const applyRewrite = async () => {
    if (!editedRewrite) return;
    setApplying(true);
    try {
      // Fork-then-apply: the backend now creates a vacancy-specific
      // CV variant and applies the accepted edits to that fork. The
      // user's DEFAULT CV is never mutated — that's the source of
      // truth all match analyses run against.
      // Use `editedRewrite` so any user tweaks made in step 2 land on
      // the fork instead of the raw AI text.
      const acceptedProfile = editedRewrite.profile ? { new: editedRewrite.profile.new } : null;
      const acceptedExperiences = (editedRewrite.experiences || []).map((e) => ({ id: e.id, new: e.new }));
      // When the user toggled the title-swap chip, send the vacancy's
      // title so the fork's `personal.title` matches. Untouched otherwise.
      const personalTitle = swapTitle && letterTitle
        ? letterTitle
        : "";
      const { data } = await api.post(`/letters/${letterId}/apply-rewrite`, {
        profile: acceptedProfile,
        experiences: acceptedExperiences,
        personal_title: personalTitle,
      });
      // Emit the fresh letter so the parent editor picks up the new
      // resume_id + resume_name (auto-forked variant).
      if (data?.letter) onLetterUpdated?.(data.letter);
      setApplied(true);
      const forkName = data?.resume?.name || t("ltRewriteFork");
      toast.success(t("ltRewriteAppliedFork", { name: forkName }));
      // Auto-advance to step 3
      setStepIdx(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("ltRewriteApplyFailed"));
    } finally {
      setApplying(false);
    }
  };

  const regenerateLetter = useCallback(async () => {
    setRegenLoading(true);
    try {
      const { data } = await api.post(`/letters/${letterId}/regenerate`);
      setResult(data);
      onLetterUpdated?.(data.letter);
      const prev = data.previous_match_score ?? 0;
      const nxt = data.letter?.match_score ?? 0;
      if (nxt - prev > 15) {
        setConfetti(true);
        toast.success(t("imwCelebration", { delta: nxt - prev }));
      }
      setStepIdx(3);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("imwRegenFailed"));
    } finally {
      setRegenLoading(false);
    }
  }, [letterId, t, onLetterUpdated]);

  const rollbackImprove = useCallback(async () => {
    if (rollingBack) return;
    setRollingBack(true);
    try {
      const { data } = await api.post(`/letters/${letterId}/rollback-improve`);
      if (data?.rolled_back) {
        toast.success(t("imwRollbackDone"));
      } else {
        toast.info(t("imwRollbackNoop"));
      }
      onLetterUpdated?.(data.letter);
      setRolledBack(true);
      // Close after a brief pause so the user sees the confirmation.
      setTimeout(() => onClose(), 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("imwRollbackFailed"));
    } finally {
      setRollingBack(false);
    }
  }, [letterId, rollingBack, t, onLetterUpdated, onClose]);

  // When entering step 2, kick off the rewrite preview once.
  useEffect(() => {
    if (stepIdx === 1 && !rewrite && !rewriteLoading) loadRewrite();
  }, [stepIdx, rewrite, rewriteLoading, loadRewrite]);

  // When entering step 3, auto-trigger regeneration.
  useEffect(() => {
    if (stepIdx === 2 && applied && !result && !regenLoading) regenerateLetter();
  }, [stepIdx, applied, result, regenLoading, regenerateLetter]);

  const busy = adviceLoading || rewriteLoading || applying || regenLoading;

  return (
    <div data-testid="improve-wizard" className="fixed inset-0 z-[70]">
      <ConfettiBurst show={confetti} onDone={() => setConfetti(false)} />
      <div className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm" onClick={busy ? undefined : onClose} />
      <div className="relative z-10 mx-auto my-6 max-w-5xl bg-[#F9F8F6] border border-neutral-900 shadow-2xl">
        <div className="sticky top-0 bg-[#F9F8F6] border-b border-neutral-900 px-6 py-4 flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <p className="ed-eyebrow flex items-center gap-2">
              <Sparkles size={12} strokeWidth={1.75} /> {t("imwTitle")}
            </p>
            <h2 className="font-serif text-2xl mt-1 leading-tight">{t("imwSubtitle")}</h2>
            <StepBar current={stepIdx} t={t} />
          </div>
          <button
            data-testid="imw-close"
            onClick={onClose}
            disabled={busy}
            className="p-2 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors shrink-0 ml-4"
          >
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>

        <div className="p-6 max-h-[65vh] overflow-y-auto" data-testid={`imw-body-${STEPS[stepIdx].key}`}>
          <PurposePanel stepKey={STEPS[stepIdx].key} t={t} />

          {stepIdx === 0 && (
            <AdviceStep
              loading={adviceLoading}
              advice={advice}
              currentScore={currentScore}
              tipContexts={tipContexts}
              onTipContextChange={(idx, patch) =>
                setTipContexts((prev) => ({
                  ...prev,
                  [idx]: { ...(prev[idx] || { input: "", experience_ids: [] }), ...patch },
                }))
              }
              t={t}
            />
          )}
          {stepIdx === 1 && (
            <RewriteStep
              loading={rewriteLoading}
              rewrite={editedRewrite}
              originalRewrite={rewrite}
              onEdit={(patch) => setEditedRewrite((prev) => prev ? { ...prev, ...patch } : prev)}
              vacancyTitle={(letterTitle || "").trim()}
              swapTitle={swapTitle}
              onToggleSwapTitle={setSwapTitle}
              tips={advice?.tips || []}
              tipContexts={tipContexts}
              t={t}
            />
          )}
          {stepIdx === 2 && (
            <RegenStep loading={regenLoading} t={t} />
          )}
          {stepIdx === 3 && result && (
            <ResultStep result={result} currentScore={currentScore} t={t} />
          )}
        </div>

        <div className="sticky bottom-0 bg-[#F9F8F6] border-t border-neutral-900 px-6 py-4 flex items-center justify-between gap-3">
          <button
            data-testid="imw-back"
            onClick={() => setStepIdx((i) => Math.max(0, i - 1))}
            disabled={stepIdx === 0 || busy || stepIdx === 3}
            className="ed-btn ed-btn-outline disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ArrowLeft size={14} strokeWidth={1.75} /> {t("imwBack")}
          </button>
          <div className="flex gap-3">
            {stepIdx === 0 && advice && (
              <button
                data-testid="imw-next-rewrite"
                onClick={() => setStepIdx(1)}
                disabled={busy || !advice.tips.some((tip) => ["wording", "experience"].includes(tip.category))}
                title={!advice.tips.some((tip) => ["wording", "experience"].includes(tip.category)) ? t("imwNoRewriteTips") : ""}
                className="ed-btn"
              >
                {t("imwGoRewrite")} <ArrowRight size={14} strokeWidth={1.75} />
              </button>
            )}
            {stepIdx === 1 && rewrite && !applying && (
              <button
                data-testid="imw-apply-rewrite"
                onClick={applyRewrite}
                disabled={applying}
                className="ed-btn"
              >
                <Check size={14} strokeWidth={1.75} /> {t("imwApplyAndRegen")}
              </button>
            )}
            {stepIdx === 3 && result && (() => {
              const newScore = result.letter?.match_score;
              const oldScore = result.previous_match_score;
              const scoreDropped =
                newScore !== null && newScore !== undefined &&
                oldScore !== null && oldScore !== undefined &&
                newScore < oldScore && !rolledBack;
              if (scoreDropped) {
                return (
                  <>
                    <button
                      data-testid="imw-rollback"
                      onClick={rollbackImprove}
                      disabled={rollingBack}
                      className="ed-btn ed-btn-outline"
                      title={t("imwRollbackHint")}
                    >
                      {rollingBack
                        ? <><Loader2 size={14} strokeWidth={1.75} className="animate-spin" /> {t("imwRollbackBusy")}</>
                        : <><RotateCcw size={14} strokeWidth={1.75} /> {t("imwRollbackCta")}</>}
                    </button>
                    <button
                      data-testid="imw-keep-anyway"
                      onClick={onClose}
                      disabled={rollingBack}
                      className="ed-btn"
                      title={t("imwKeepAnywayHint")}
                    >
                      <Check size={14} strokeWidth={1.75} /> {t("imwKeepAnywayCta")}
                    </button>
                  </>
                );
              }
              return (
                <button data-testid="imw-finish" onClick={onClose} className="ed-btn">
                  <Check size={14} strokeWidth={1.75} /> {t("imwFinish")}
                </button>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Sub-components ----

function StepBar({ current, t }) {
  return (
    <div className="flex items-center gap-2 mt-3 flex-wrap" data-testid="imw-stepbar">
      {STEPS.map((s, i) => {
        const state = i < current ? "done" : i === current ? "active" : "todo";
        const cls =
          state === "active"
            ? "border-neutral-900 bg-neutral-900 text-[#F9F8F6]"
            : state === "done"
            ? "border-emerald-700 bg-emerald-50 text-emerald-800"
            : "border-neutral-300 text-neutral-400";
        return (
          <div
            key={s.key}
            data-testid={`imw-step-${s.key}-${state}`}
            className={`border px-2.5 py-1 font-mono text-[0.6rem] uppercase tracking-[0.15em] inline-flex items-center gap-1.5 ${cls}`}
          >
            <s.Icon size={11} strokeWidth={1.75} /> {i + 1}. {t(s.labelKey)}
          </div>
        );
      })}
    </div>
  );
}

function PurposePanel({ stepKey, t }) {
  return (
    <div className="border-l-4 border-neutral-900 bg-neutral-50 p-4 mb-6 flex gap-3" data-testid={`imw-purpose-${stepKey}`}>
      <Info size={16} strokeWidth={1.75} className="shrink-0 mt-0.5 text-neutral-700" />
      <div>
        <p className="ed-eyebrow mb-1">{t(`imwPurpose_${stepKey}_title`)}</p>
        <p className="text-sm text-neutral-700 leading-relaxed">{t(`imwPurpose_${stepKey}_body`)}</p>
      </div>
    </div>
  );
}

function AdviceStep({ loading, advice, currentScore, tipContexts = {}, onTipContextChange, t }) {
  if (loading) return <LoadingBlock label={t("ltImproveLoading")} />;
  if (!advice) return null;
  const experiences = advice.experiences || [];
  return (
    <>
      {(currentScore !== null && currentScore !== undefined && advice.projected_score !== null) && (
        <div className="ed-card p-5 mb-6 flex items-center gap-5" data-testid="imw-projection">
          <div className="flex flex-col items-center gap-1.5">
            <MatchScoreBadge score={currentScore} size="lg" />
            <span className="text-[0.65rem] font-mono uppercase tracking-[0.15em] text-neutral-500">{t("ltImproveNow")}</span>
          </div>
          <TrendingUp size={22} strokeWidth={1.5} className="text-emerald-700 shrink-0" />
          <div className="flex flex-col items-center gap-1.5">
            <MatchScoreBadge score={advice.projected_score} size="lg" />
            <span className="text-[0.65rem] font-mono uppercase tracking-[0.15em] text-neutral-500">{t("ltImproveProjected")}</span>
          </div>
          {advice.headline && (
            <p className="font-serif text-base italic text-neutral-800 flex-1 leading-relaxed">
              {advice.headline}
            </p>
          )}
        </div>
      )}
      <ol className="space-y-4" data-testid="imw-tip-list">
        {advice.tips.map((tip, i) => {
          const Icon = CATEGORY_ICONS[tip.category] || Lightbulb;
          const ctx = tipContexts[i] || { input: "", experience_ids: [] };
          const hasContext = (ctx.input || "").trim().length > 0 || (ctx.experience_ids || []).length > 0;
          const isFromEmployer = tip.source === "employer_clarification";
          return (
            <li
              key={i}
              data-testid={`imw-tip-${i}`}
              className={`ed-card p-5 ${isFromEmployer ? "border-2 border-[#F5B478] bg-[#FEF6EC]" : ""}`}
            >
              <div className="flex gap-4">
                <div className={`shrink-0 border p-2.5 self-start ${isFromEmployer ? "border-[#7A3A00] bg-[#FEE8D3]" : "border-neutral-900"}`}>
                  <Icon size={18} strokeWidth={1.5} className={isFromEmployer ? "text-[#7A3A00]" : ""} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap mb-1">
                    {isFromEmployer ? (
                      <span
                        data-testid={`imw-tip-${i}-employer-badge`}
                        className="inline-flex items-center gap-1 font-mono text-[0.6rem] uppercase tracking-[0.15em] bg-[#7A3A00] text-white px-2 py-0.5"
                        title={t("ltTipEmployerBadgeHint")}
                      >
                        <HelpCircle size={10} strokeWidth={2} /> {t("ltTipEmployerBadge")}
                      </span>
                    ) : (
                      <span className="font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-500">
                        {t(`ltImproveCat_${tip.category}`)}
                      </span>
                    )}
                    {typeof tip.impact === "number" && (
                      <span className="font-mono text-[0.65rem] uppercase tracking-[0.15em] border border-emerald-700 bg-emerald-50 text-emerald-800 px-2 py-0.5">
                        +{tip.impact} {t("ltImproveImpactUnit")}
                      </span>
                    )}
                    {hasContext && (
                      <span
                        data-testid={`imw-tip-${i}-context-badge`}
                        className="inline-flex items-center gap-1 font-mono text-[0.55rem] uppercase tracking-[0.15em] bg-neutral-900 text-white px-2 py-0.5"
                        title={t("ltTipContextBadgeHint")}
                      >
                        <Check size={9} strokeWidth={2} /> {t("ltTipContextBadge")}
                      </span>
                    )}
                  </div>
                  <h3 className="font-serif text-lg leading-tight">{tip.title}</h3>
                  <p className="text-sm text-neutral-700 mt-2 leading-relaxed whitespace-pre-wrap">{tip.detail}</p>
                </div>
              </div>
              <TipContextPanel
                tipIndex={i}
                context={ctx}
                experiences={experiences}
                onChange={(patch) => onTipContextChange?.(i, patch)}
                t={t}
              />
            </li>
          );
        })}
      </ol>
    </>
  );
}

/**
 * Collapsible "Toelichting geven" panel that lives inside every tip
 * card. Provides:
 *   - A textarea (with optional speech-to-text via Web Speech API)
 *     so the user can add context in their own words.
 *   - A chip-multiselect of the user's work experiences (from their
 *     default CV) so the input is scoped to the right roles.
 * Both signals are forwarded to the parent's `tipContexts` and end up
 * as a "User note (applies to Role@Company): …" line in the rewrite
 * prompt (see letter_ai.py::ai_rewrite_resume_for_match).
 */
function TipContextPanel({ tipIndex, context, experiences, onChange, t }) {
  const [open, setOpen] = useState(false);
  // Web Speech API is browser-native — no library, no backend call.
  // Not every browser exposes it (Safari desktop currently doesn't in
  // full form), so we feature-detect and hide the mic when absent.
  const SpeechRecognitionCtor = typeof window !== "undefined"
    ? (window.SpeechRecognition || window.webkitSpeechRecognition)
    : null;
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);

  const startListening = () => {
    if (!SpeechRecognitionCtor) return;
    try {
      const rec = new SpeechRecognitionCtor();
      rec.lang = document.documentElement.lang === "en" ? "en-US" : "nl-NL";
      rec.interimResults = true;
      rec.continuous = true;
      // Baseline text captured before user starts talking so we can
      // append the transcript instead of overwriting whatever they
      // already typed.
      const baseline = context.input || "";
      let interim = "";
      let final = "";
      rec.onresult = (event) => {
        interim = "";
        for (let idx = event.resultIndex; idx < event.results.length; idx++) {
          const res = event.results[idx];
          if (res.isFinal) final += res[0].transcript;
          else interim += res[0].transcript;
        }
        const combined = (baseline + " " + final + interim).trim();
        onChange?.({ input: combined });
      };
      rec.onerror = () => setListening(false);
      rec.onend = () => setListening(false);
      recognitionRef.current = rec;
      rec.start();
      setListening(true);
    } catch (e) {
      setListening(false);
    }
  };
  const stopListening = () => {
    try { recognitionRef.current?.stop(); } catch (e) { /* ignore */ }
    setListening(false);
  };
  useEffect(() => () => {
    // On unmount: kill any dangling recognition session.
    try { recognitionRef.current?.stop(); } catch (e) { /* ignore */ }
  }, []);

  const toggleExperience = (expId) => {
    const cur = context.experience_ids || [];
    if (cur.includes(expId)) {
      onChange?.({ experience_ids: cur.filter((x) => x !== expId) });
    } else {
      onChange?.({ experience_ids: [...cur, expId] });
    }
  };

  const selectedSet = new Set(context.experience_ids || []);
  return (
    <div className="mt-4 pl-14" data-testid={`imw-tip-${tipIndex}-context-panel`}>
      {!open ? (
        <button
          type="button"
          data-testid={`imw-tip-${tipIndex}-context-toggle`}
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 font-mono text-[0.62rem] uppercase tracking-[0.15em] border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white px-3 py-1.5 transition-colors"
        >
          <PenLine size={11} strokeWidth={1.75} />
          {(context.input || "").trim() || (context.experience_ids || []).length
            ? t("ltTipContextEdit")
            : t("ltTipContextCta")}
        </button>
      ) : (
        <div className="border border-neutral-900 bg-neutral-50 p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-mono text-[0.62rem] uppercase tracking-[0.15em] text-neutral-700">
                {t("ltTipContextTitle")}
              </p>
              <p className="text-xs text-neutral-600 mt-0.5 leading-snug">
                {t("ltTipContextHint")}
              </p>
            </div>
            <button
              type="button"
              data-testid={`imw-tip-${tipIndex}-context-close`}
              onClick={() => setOpen(false)}
              className="text-neutral-500 hover:text-neutral-900"
              aria-label={t("ltTipContextClose")}
            >
              <X size={14} strokeWidth={1.75} />
            </button>
          </div>
          <div className="relative">
            <textarea
              data-testid={`imw-tip-${tipIndex}-context-input`}
              value={context.input || ""}
              onChange={(e) => onChange?.({ input: e.target.value })}
              rows={3}
              maxLength={800}
              placeholder={t("ltTipContextPh")}
              className="w-full border border-neutral-300 focus:border-neutral-900 outline-none px-3 py-2 text-sm leading-relaxed resize-y bg-white pr-10"
            />
            {SpeechRecognitionCtor && (
              <button
                type="button"
                data-testid={`imw-tip-${tipIndex}-context-mic`}
                onClick={listening ? stopListening : startListening}
                className={`absolute right-2 top-2 p-1.5 border transition-colors ${
                  listening
                    ? "border-rose-600 bg-rose-600 text-white animate-pulse"
                    : "border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white"
                }`}
                title={listening ? t("ltTipContextMicStop") : t("ltTipContextMicStart")}
                aria-label={listening ? t("ltTipContextMicStop") : t("ltTipContextMicStart")}
              >
                {listening ? <MicOff size={13} strokeWidth={1.75} /> : <Mic size={13} strokeWidth={1.75} />}
              </button>
            )}
          </div>
          {listening && (
            <p className="text-[0.7rem] text-rose-700 font-mono uppercase tracking-[0.15em]">
              {t("ltTipContextMicRecording")}
            </p>
          )}
          {experiences.length > 0 && (
            <div className="space-y-1.5">
              <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500">
                {t("ltTipContextExpLabel")}
              </p>
              <div className="flex flex-wrap gap-1.5" data-testid={`imw-tip-${tipIndex}-context-exps`}>
                {experiences.map((exp) => {
                  const active = selectedSet.has(exp.id);
                  const label = [exp.role, exp.company].filter(Boolean).join(" · ") || "?";
                  return (
                    <button
                      key={exp.id}
                      type="button"
                      data-testid={`imw-tip-${tipIndex}-context-exp-${exp.id}`}
                      onClick={() => toggleExperience(exp.id)}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 text-[0.7rem] leading-tight border transition-colors ${
                        active
                          ? "bg-neutral-900 border-neutral-900 text-white"
                          : "border-neutral-300 text-neutral-700 hover:border-neutral-900"
                      }`}
                    >
                      {active && <Check size={10} strokeWidth={2} />}
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Per-tip fit-scoring summary shown at the top of step 2. Each tip's
 * criterion gets a "before → after" percentage so the user can see
 * exactly how much the rewrite (and any toelichting they added) moved
 * the needle for each individual criterion. Highlights tips where the
 * candidate provided extra context — those are the ones where "did my
 * input actually help?" is the question the user is asking.
 */
function TipScoresCard({ tipScores, tips = [], tipContexts = {}, rewriteProfile, rewriteExperiences = [], t }) {
  // Map each score to the diff row it likely corresponds to. Priority:
  //   1. If the user linked the tip to specific experience_ids, use the
  //      first one that has a diff row (index in `rewriteExperiences`).
  //   2. Else fall back to the profile diff (if any).
  //   3. Else the first experience diff.
  //   4. Else no target — click is a no-op, row shows as non-interactive.
  const expIdxById = new Map(rewriteExperiences.map((e, i) => [e.id, i]));
  const resolveTargetTestId = (tipIdx) => {
    const ctx = tipContexts[tipIdx];
    if (ctx?.experience_ids?.length) {
      for (const expId of ctx.experience_ids) {
        if (expIdxById.has(expId)) return `imw-diff-exp-${expIdxById.get(expId)}`;
      }
    }
    if (rewriteProfile) return "imw-diff-profile";
    if (rewriteExperiences.length > 0) return "imw-diff-exp-0";
    return null;
  };

  const scrollToDiff = (testId) => {
    if (!testId || typeof document === "undefined") return;
    const node = document.querySelector(`[data-testid="${testId}"]`);
    if (!node) return;
    node.scrollIntoView({ behavior: "smooth", block: "start" });
    // Brief highlight so the user visually confirms the jump landed.
    node.classList.add("ring-2", "ring-emerald-500", "ring-offset-2");
    setTimeout(() => {
      node.classList.remove("ring-2", "ring-emerald-500", "ring-offset-2");
    }, 1400);
  };
  // Sort by delta desc so the biggest wins are at the top; ties keep
  // original tip order (Array.prototype.sort is stable in modern V8).
  const enriched = tipScores
    .map((s) => ({
      ...s,
      title: tips[s.tip_index]?.title || `${t("ltImproveTipFallback")} ${s.tip_index + 1}`,
      hasUserContext: Boolean(
        (tipContexts[s.tip_index]?.input || "").trim()
        || (tipContexts[s.tip_index]?.experience_ids || []).length
      ),
    }))
    .sort((a, b) => (b.delta || 0) - (a.delta || 0));

  const totalDelta = enriched.reduce((sum, s) => sum + (s.delta || 0), 0);
  const withContext = enriched.filter((s) => s.hasUserContext);

  return (
    <div className="ed-card p-5 space-y-3" data-testid="imw-tip-scores">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="font-mono text-[0.62rem] uppercase tracking-[0.15em] text-neutral-500">
            {t("ltRewriteTipScoresEyebrow")}
          </p>
          <p className="font-serif text-lg text-neutral-900 mt-1 leading-tight">
            {t("ltRewriteTipScoresTitle")}
          </p>
          {withContext.length > 0 && (
            <p className="text-xs text-neutral-600 mt-1.5 leading-snug">
              {t("ltRewriteTipScoresContextNote").replace("{n}", withContext.length)}
            </p>
          )}
        </div>
        <div className="text-right shrink-0">
          <p className="font-mono text-[0.55rem] uppercase tracking-[0.15em] text-neutral-500">
            {t("ltRewriteTipScoresAvgLabel")}
          </p>
          <p
            className={`font-serif text-2xl leading-none mt-0.5 ${
              totalDelta >= 0 ? "text-emerald-700" : "text-rose-700"
            }`}
            data-testid="imw-tip-scores-total-delta"
          >
            {totalDelta >= 0 ? "+" : ""}{totalDelta}
          </p>
        </div>
      </div>
      <ul className="divide-y divide-neutral-200" data-testid="imw-tip-scores-list">
        {enriched.map((s) => {
          const target = resolveTargetTestId(s.tip_index);
          return (
            <li
              key={s.tip_index}
              data-testid={`imw-tip-score-${s.tip_index}`}
              className={`py-2 flex items-center gap-3 flex-wrap ${
                target ? "cursor-pointer hover:bg-neutral-50 -mx-2 px-2 transition-colors" : ""
              }`}
              onClick={() => target && scrollToDiff(target)}
              role={target ? "button" : undefined}
              tabIndex={target ? 0 : undefined}
              onKeyDown={(e) => {
                if (target && (e.key === "Enter" || e.key === " ")) {
                  e.preventDefault();
                  scrollToDiff(target);
                }
              }}
              title={target ? t("ltRewriteTipScoresJumpHint") : undefined}
            >
              <span className="text-sm text-neutral-800 flex-1 min-w-[10rem] leading-tight">
                {s.title}
                {s.hasUserContext && (
                  <span
                    className="ml-2 inline-flex items-center gap-1 font-mono text-[0.55rem] uppercase tracking-[0.15em] bg-neutral-900 text-white px-1.5 py-0.5"
                    title={t("ltRewriteTipScoresContextChipHint")}
                  >
                    <Check size={9} strokeWidth={2} /> {t("ltTipContextBadge")}
                  </span>
                )}
              </span>
              <div className="flex items-center gap-2 shrink-0">
                <span className="font-mono text-xs text-neutral-500 min-w-[2.2rem] text-right">{s.before}%</span>
                <ArrowRight size={12} strokeWidth={1.75} className="text-neutral-400" />
                <span className="font-mono text-xs text-neutral-900 min-w-[2.2rem] text-right">{s.after}%</span>
                <span
                  className={`font-mono text-[0.6rem] uppercase tracking-[0.15em] px-2 py-0.5 border min-w-[3rem] text-center ${
                    s.delta > 0
                      ? "border-emerald-700 bg-emerald-50 text-emerald-800"
                      : s.delta < 0
                      ? "border-rose-500 bg-rose-50 text-rose-800"
                      : "border-neutral-300 bg-neutral-50 text-neutral-500"
                  }`}
                >
                  {s.delta > 0 ? "+" : ""}{s.delta}
                </span>
              </div>
              {s.note && (
                <p className="w-full text-xs text-neutral-600 italic pl-2 border-l-2 border-neutral-300 mt-1">
                  {s.note}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}


function RewriteStep({ loading, rewrite, originalRewrite, onEdit, vacancyTitle, swapTitle, onToggleSwapTitle, tips = [], tipContexts = {}, t }) {
  if (loading) return <LoadingBlock label={t("ltRewriteLoading")} />;
  if (!rewrite) return null;
  // Only surface the title-swap chip when the default CV actually has a
  // title AND it differs from the vacancy title. Same-title case → no
  // toggle (would just be visual noise).
  const currentTitle = (rewrite.personal_title || "").trim();
  const targetTitle = (vacancyTitle || "").trim();
  const canSwapTitle = Boolean(
    currentTitle && targetTitle && currentTitle.toLowerCase() !== targetTitle.toLowerCase()
  );
  const tipScores = Array.isArray(rewrite.tip_scores) ? rewrite.tip_scores : [];
  return (
    <div className="space-y-6">
      {tipScores.length > 0 && (
        <TipScoresCard
          tipScores={tipScores}
          tips={tips}
          tipContexts={tipContexts}
          rewriteProfile={rewrite.profile}
          rewriteExperiences={rewrite.experiences || []}
          t={t}
        />
      )}
      {canSwapTitle && (
        <div
          className="ed-card p-4 flex flex-wrap items-start gap-3 bg-emerald-50 border-emerald-300"
          data-testid="imw-title-swap-card"
        >
          <label className="flex items-start gap-3 cursor-pointer flex-1 min-w-0">
            <input
              type="checkbox"
              data-testid="imw-title-swap-toggle"
              checked={!!swapTitle}
              onChange={(e) => onToggleSwapTitle?.(e.target.checked)}
              className="mt-1 h-4 w-4 accent-neutral-900 cursor-pointer"
            />
            <span className="min-w-0">
              <span className="block font-mono text-[0.62rem] uppercase tracking-[0.15em] text-emerald-800">
                {t("ltRewriteTitleSwapEyebrow")}
              </span>
              <span className="block font-serif text-base text-neutral-900 leading-snug mt-1">
                {t("ltRewriteTitleSwapAsk")}
              </span>
              <span className="block text-sm text-neutral-700 mt-2 leading-relaxed">
                <span className="line-through text-neutral-500">"{currentTitle}"</span>
                <span className="mx-1.5 font-mono text-[0.62rem] uppercase tracking-[0.15em] text-neutral-500">→</span>
                <span className="font-semibold text-emerald-800">"{targetTitle}"</span>
              </span>
              <span className="block text-[0.72rem] text-neutral-600 mt-2 italic">
                {t("ltRewriteTitleSwapHint")}
              </span>
            </span>
          </label>
        </div>
      )}
      {rewrite.profile && (
        <DiffRow
          testId="imw-diff-profile"
          label={t("ltRewriteProfileLabel")}
          meta=""
          oldText={rewrite.profile.old}
          newText={rewrite.profile.new}
          aiSuggestion={originalRewrite?.profile?.new || ""}
          onNewTextChange={(nv) =>
            onEdit?.({ profile: { ...rewrite.profile, new: nv } })
          }
          groundedIn={rewrite.profile.grounded_in}
          compensates={rewrite.profile.compensates}
          t={t}
        />
      )}
      {(rewrite.experiences || []).map((e, i) => (
        <DiffRow
          key={e.id}
          testId={`imw-diff-exp-${i}`}
          label={t("ltRewriteExpLabel")}
          meta={`${e.role || "?"} · ${e.company || "?"}`}
          oldText={e.old}
          newText={e.new}
          aiSuggestion={
            (originalRewrite?.experiences || []).find((oe) => oe.id === e.id)?.new || ""
          }
          onNewTextChange={(nv) =>
            onEdit?.({
              experiences: rewrite.experiences.map((x) =>
                x.id === e.id ? { ...x, new: nv } : x
              ),
            })
          }
          groundedIn={e.grounded_in}
          compensates={e.compensates}
          t={t}
        />
      ))}
    </div>
  );
}

function RegenStep({ loading, t }) {
  const ESTIMATE = 30; // seconds — matches ai_generate_letter typical duration
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!loading) { setElapsed(0); return; }
    const start = Date.now();
    const timer = setInterval(() => {
      setElapsed(Math.floor((Date.now() - start) / 1000));
    }, 250);
    return () => clearInterval(timer);
  }, [loading]);

  // Progress caps at 95% until the response actually arrives — the last 5% is
  // reserved so the bar never looks "done" while we're still waiting.
  const pct = Math.min(95, Math.round((elapsed / ESTIMATE) * 100));
  const overtime = elapsed > ESTIMATE;

  return (
    <div className="py-12 text-center" data-testid="imw-regen-body">
      {loading ? (
        <>
          <Loader2 size={28} className="mx-auto animate-spin text-neutral-500" />
          <p className="ed-eyebrow mt-4">{t("imwRegenLoading")}</p>
          <div className="mt-6 max-w-md mx-auto" data-testid="imw-regen-progress">
            <div className="h-2 border border-neutral-900 bg-neutral-100 overflow-hidden">
              <div
                className={`h-full transition-all duration-300 ${overtime ? "bg-amber-500" : "bg-emerald-600"}`}
                style={{ width: `${pct}%` }}
                data-testid="imw-regen-progress-bar"
              />
            </div>
            <div className="flex items-center justify-between mt-2 font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-600">
              <span data-testid="imw-regen-elapsed">{elapsed}s</span>
              <span>{overtime ? t("imwRegenOvertime") : `~${ESTIMATE}s`}</span>
            </div>
          </div>
          <p className="text-sm text-neutral-500 mt-4 max-w-md mx-auto leading-relaxed">
            {t("imwRegenPatience")}
          </p>
        </>
      ) : (
        <p className="text-sm text-neutral-500 italic">{t("imwRegenPending")}</p>
      )}
    </div>
  );
}

function ResultStep({ result, currentScore, t }) {
  const newScore = result.letter?.match_score;
  const oldScore = result.previous_match_score;
  const delta = (newScore !== null && oldScore !== null) ? newScore - oldScore : null;
  const scoreDropped = delta !== null && delta < 0;
  return (
    <div className="space-y-6" data-testid="imw-result-body">
      {scoreDropped && (
        <div
          data-testid="imw-result-dropped-banner"
          className="border-2 border-rose-600 bg-rose-50 p-5 flex gap-4 items-start"
        >
          <Info size={22} strokeWidth={1.5} className="text-rose-700 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="font-mono text-[0.62rem] uppercase tracking-[0.15em] text-rose-800">
              {t("imwResultDroppedEyebrow")}
            </p>
            <p className="font-serif text-lg text-neutral-900 mt-1 leading-snug">
              {t("imwResultDroppedTitle").replace("{delta}", delta)}
            </p>
            <p className="text-sm text-neutral-700 mt-2 leading-relaxed">
              {t("imwResultDroppedBody")}
            </p>
          </div>
        </div>
      )}
      <div className="ed-card p-6 flex items-center gap-6" data-testid="imw-result-scoreband">
        {(oldScore !== null && oldScore !== undefined) && (
          <>
            <div className="flex flex-col items-center gap-1.5">
              <MatchScoreBadge score={oldScore} size="lg" />
              <span className="text-[0.65rem] font-mono uppercase tracking-[0.15em] text-neutral-500">{t("imwResultBefore")}</span>
            </div>
            <TrendingUp size={22} strokeWidth={1.5} className="text-emerald-700 shrink-0" />
          </>
        )}
        <div className="flex flex-col items-center gap-1.5">
          <MatchScoreBadge score={newScore} size="lg" />
          <span className="text-[0.65rem] font-mono uppercase tracking-[0.15em] text-neutral-500">{t("imwResultAfter")}</span>
        </div>
        {delta !== null && (
          <div className={`ml-4 font-mono text-sm px-3 py-1.5 border ${delta > 0 ? "border-emerald-700 bg-emerald-50 text-emerald-800" : delta < 0 ? "border-rose-700 bg-rose-50 text-rose-800" : "border-neutral-300 text-neutral-500"}`}>
            {delta > 0 ? "+" : ""}{delta} {t("imwResultDelta")}
          </div>
        )}
      </div>
      {result.letter?.match_summary && (
        <div className="ed-card p-5">
          <p className="ed-eyebrow mb-2">{t("ltMatchScore")}</p>
          <p className="font-serif text-base text-neutral-800 leading-relaxed">{result.letter.match_summary}</p>
        </div>
      )}
      <div className="ed-card p-5">
        <p className="ed-eyebrow mb-3 flex items-center gap-2"><RefreshCw size={11} strokeWidth={1.75} /> {t("imwResultLetterPreview")}</p>
        <pre className="font-serif text-sm text-neutral-800 leading-relaxed whitespace-pre-wrap max-h-64 overflow-y-auto">
          {(result.letter?.letter_text || "").slice(0, 800)}
          {(result.letter?.letter_text || "").length > 800 ? "…" : ""}
        </pre>
      </div>
    </div>
  );
}

function LoadingBlock({ label }) {
  return (
    <div className="py-16 text-center">
      <Loader2 size={28} className="mx-auto animate-spin text-neutral-500" />
      <p className="ed-eyebrow mt-4">{label}</p>
    </div>
  );
}

function DiffRow({ label, meta, oldText, newText, aiSuggestion, onNewTextChange, testId, t, groundedIn, compensates }) {
  const [openSources, setOpenSources] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(newText || "");

  // Whenever the parent hands us a fresh newText (e.g. rewrite reloaded),
  // sync the local draft so the textarea reflects the truth-of-state.
  useEffect(() => { setDraft(newText || ""); }, [newText]);

  const grounded = Array.isArray(groundedIn) ? groundedIn : [];
  const comp = Array.isArray(compensates) ? compensates : [];
  // "Edited" = user has drifted from the AI's original wording. Shown as
  // a small badge and unlocks the "Herstel origineel" button.
  const isEdited = Boolean(aiSuggestion) && (newText || "").trim() !== aiSuggestion.trim();

  const commitDraft = () => {
    setEditing(false);
    if (draft !== newText) onNewTextChange?.(draft);
  };
  const cancelDraft = () => {
    setDraft(newText || "");
    setEditing(false);
  };
  const resetToAi = () => {
    if (!aiSuggestion) return;
    setDraft(aiSuggestion);
    onNewTextChange?.(aiSuggestion);
    setEditing(false);
  };

  return (
    <div className="ed-card p-5" data-testid={testId}>
      <div className="flex items-center gap-3 flex-wrap mb-4">
        <span className="font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-500">{label}</span>
        {meta && <span className="font-serif italic text-sm text-neutral-700">{meta}</span>}
        {isEdited && (
          <span
            data-testid={`${testId}-edited-badge`}
            className="inline-flex items-center gap-1 border border-neutral-900 bg-neutral-900 text-white px-2 py-0.5 font-mono text-[0.55rem] uppercase tracking-[0.15em]"
            title={t("ltRewriteEditedHint")}
          >
            <PenLine size={9} strokeWidth={1.75} /> {t("ltRewriteEditedLabel")}
          </span>
        )}
        {comp.map((c, i) => (
          <span
            key={i}
            data-testid={`${testId}-compensates-${i}`}
            className="inline-flex items-center gap-1 border border-emerald-800 bg-emerald-700 text-white px-2 py-0.5 font-mono text-[0.55rem] uppercase tracking-[0.15em]"
            title={t("ltRewriteCompensatesHint")}
          >
            <ShieldCheck size={9} strokeWidth={1.75} /> {t("ltRewriteCompensatesLabel")}: {c}
          </span>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <p className="ed-eyebrow mb-2 text-neutral-500">{t("ltRewriteBefore")}</p>
          <div className="border border-neutral-200 bg-neutral-50 p-3 text-sm text-neutral-600 leading-relaxed whitespace-pre-wrap min-h-[6rem]">
            {oldText || "—"}
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
            <p className="ed-eyebrow text-emerald-700 flex items-center gap-1.5">
              <RefreshCw size={11} strokeWidth={1.75} /> {t("ltRewriteAfter")}
            </p>
            <div className="flex items-center gap-1.5 flex-wrap">
              {isEdited && aiSuggestion && !editing && (
                <button
                  type="button"
                  data-testid={`${testId}-reset-ai`}
                  onClick={resetToAi}
                  className="font-mono text-[0.55rem] uppercase tracking-[0.15em] inline-flex items-center gap-1 border border-neutral-400 bg-white hover:border-neutral-900 hover:bg-neutral-900 hover:text-white px-2 py-0.5 transition-colors"
                  title={t("ltRewriteResetHint")}
                >
                  <RotateCcw size={10} strokeWidth={1.75} /> {t("ltRewriteResetCta")}
                </button>
              )}
              {!editing && (
                <button
                  type="button"
                  data-testid={`${testId}-edit-toggle`}
                  onClick={() => setEditing(true)}
                  className="font-mono text-[0.55rem] uppercase tracking-[0.15em] inline-flex items-center gap-1 border border-neutral-900 bg-white hover:bg-neutral-900 hover:text-white px-2 py-0.5 transition-colors"
                  title={t("ltRewriteEditHint")}
                >
                  <Pencil size={10} strokeWidth={1.75} /> {t("ltRewriteEditCta")}
                </button>
              )}
              {grounded.length > 0 && !editing && (
                <button
                  type="button"
                  data-testid={`${testId}-sources-toggle`}
                  onClick={() => setOpenSources((v) => !v)}
                  className="font-mono text-[0.55rem] uppercase tracking-[0.15em] inline-flex items-center gap-1 border border-neutral-900 bg-white hover:bg-neutral-900 hover:text-white px-2 py-0.5 transition-colors"
                  title={t("ltRewriteSourcesHint")}
                >
                  <BookOpen size={10} strokeWidth={1.75} /> {t("ltRewriteSourcesCta")} ({grounded.length})
                </button>
              )}
            </div>
          </div>
          {editing ? (
            <>
              <textarea
                data-testid={`${testId}-edit-textarea`}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                autoFocus
                rows={Math.max(6, Math.min(14, (draft || "").split("\n").length + 2))}
                className="w-full border border-emerald-700 bg-white p-3 text-sm text-neutral-900 leading-relaxed focus:outline-none focus:ring-1 focus:ring-emerald-700"
              />
              <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                <button
                  type="button"
                  data-testid={`${testId}-edit-save`}
                  onClick={commitDraft}
                  className="font-mono text-[0.6rem] uppercase tracking-[0.15em] inline-flex items-center gap-1 bg-emerald-700 hover:bg-emerald-800 text-white px-3 py-1.5"
                >
                  <Check size={11} strokeWidth={2} /> {t("ltRewriteEditSave")}
                </button>
                <button
                  type="button"
                  data-testid={`${testId}-edit-cancel`}
                  onClick={cancelDraft}
                  className="font-mono text-[0.6rem] uppercase tracking-[0.15em] inline-flex items-center gap-1 border border-neutral-400 bg-white hover:border-neutral-900 px-3 py-1.5"
                >
                  <X size={11} strokeWidth={2} /> {t("ltRewriteEditCancel")}
                </button>
                {aiSuggestion && draft.trim() !== aiSuggestion.trim() && (
                  <button
                    type="button"
                    data-testid={`${testId}-edit-reset-ai`}
                    onClick={resetToAi}
                    className="font-mono text-[0.6rem] uppercase tracking-[0.15em] inline-flex items-center gap-1 border border-neutral-400 bg-white hover:border-neutral-900 px-3 py-1.5"
                  >
                    <RotateCcw size={11} strokeWidth={1.75} /> {t("ltRewriteResetCta")}
                  </button>
                )}
              </div>
            </>
          ) : (
            <div className="border border-emerald-700 bg-emerald-50/60 p-3 text-sm text-neutral-900 leading-relaxed whitespace-pre-wrap min-h-[6rem]">
              {newText || "—"}
            </div>
          )}
          {openSources && grounded.length > 0 && !editing && (
            <div
              data-testid={`${testId}-sources-list`}
              className="mt-2 border border-neutral-900 bg-white p-3 space-y-1.5"
            >
              <p className="font-mono text-[0.55rem] uppercase tracking-[0.15em] text-neutral-500">{t("ltRewriteSourcesTitle")}</p>
              {grounded.map((s, i) => (
                <p key={i} data-testid={`${testId}-source-${i}`} className="text-xs text-neutral-700 leading-relaxed border-l-2 border-neutral-900 pl-3 italic">
                  "{s}"
                </p>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
